import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import { useApp } from "./AppContext";
import { useSalon } from "./SalonContext";
import { useFeatureAccess } from "./FeatureAccessContext";
import { getApiUrl } from "../config/api";
import { isMessagingEnabled } from "../utils/messagingFlag";
import { encodeConversationId } from "../utils/conversationId";
import { applyMessageStatus } from "../components/chat/chatHelpers";
import {
  MESSAGING_REALTIME_EVENT,
  MessagingRealtimePayload,
} from "../utils/messagingRealtime";
import type {
  Conversation,
  Message,
  MessageMediaRef,
  ScheduledMessage,
  UpdateScheduledRequest,
  ListConversationsResponse,
  ThreadResponse,
  SendMessageResponse,
  ReadStateResponse,
  AssignClientResponse,
  CreateConversationResponse,
  UploadMediaResponse,
  ListScheduledResponse,
  ScheduledResponse,
} from "../types/messaging";
import type { AppointmentScheduledMessage } from "../types/appointmentScheduledMessages";

/** Read a File as a bare base64 string (strips the `data:...;base64,` prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Data layer for the two-way SMS/MMS Chat inbox.
 *
 * Mirrors the VoiceAIContext pattern: it reads the acting location from
 * SalonContext (`businessInfo.id` is the business-location UUID) and talks to
 * the messaging microservice via `fetchWithAuth(getApiUrl(...))`. The Chat
 * screens (6b) and interaction overlays (6c) consume this; real-time Pusher
 * updates are wired on top in 6b. Everything is gated upstream by
 * `REACT_APP_MESSAGING_ENABLED` + `messaging.canView` + `messaging_platform`.
 */

interface FetchConversationsOptions {
  limit?: number;
  offset?: number;
}

interface FetchThreadOptions {
  limit?: number;
  /** ISO timestamp of the oldest loaded message, to page further back. */
  before?: string;
}

interface MessagingContextType {
  // Inbox list
  conversations: Conversation[];
  conversationsLoading: boolean;
  /** Shared unread conversation count for this location (nav badge). */
  totalUnreadCount: number;
  fetchConversations: (options?: FetchConversationsOptions) => Promise<void>;

  // Active thread
  activeConversation: Conversation | null;
  messages: Message[];
  /** True while opening/switching to a thread — drives the branded feed overlay. */
  messagesLoading: boolean;
  /** True only while a "load earlier" page is in flight (button spinner). */
  loadingEarlier: boolean;
  hasMoreMessages: boolean;
  fetchThread: (
    conversationId: string,
    options?: FetchThreadOptions,
  ) => Promise<void>;
  clearThread: () => void;

  // Mutations
  sendMessage: (
    conversationId: string,
    body: string,
    media?: MessageMediaRef[],
  ) => Promise<SendMessageResponse>;
  setReadState: (conversationId: string, isUnread: boolean) => Promise<void>;
  assignClient: (
    conversationId: string,
    clientUuid: string | null,
  ) => Promise<void>;

  // New chat + MMS upload (6c)
  createConversation: (
    phone: string,
    clientUuid?: string | null,
  ) => Promise<Conversation>;
  uploadMedia: (file: File) => Promise<MessageMediaRef>;

  // Scheduled sends (6c) — the per-conversation overlay drives these; the panel
  // holds its own list state, so these return the affected row(s) directly.
  listScheduled: (conversationId: string) => Promise<{
    scheduled: ScheduledMessage[];
    appointmentScheduled: AppointmentScheduledMessage[];
  }>;
  createScheduled: (
    conversationId: string,
    body: string,
    scheduledAt: string,
  ) => Promise<ScheduledMessage>;
  updateScheduled: (
    scheduledId: string,
    patch: UpdateScheduledRequest,
  ) => Promise<ScheduledMessage>;
  cancelScheduled: (scheduledId: string) => Promise<ScheduledMessage>;
  sendScheduledNow: (scheduledId: string) => Promise<ScheduledMessage>;
}

const MessagingContext = createContext<MessagingContextType | undefined>(
  undefined,
);

export function MessagingProvider({ children }: { children: ReactNode }) {
  const { fetchWithAuth } = useApp();
  // SalonContext is untyped JS; cast to the field we need (the acting
  // location's UUID), mirroring CashDrawerPage / GiftCardsSubTab.
  const { businessInfo } = useSalon() as {
    businessInfo: { id?: string } | null;
  };
  const { access, messagingPlatform } = useFeatureAccess();
  const businessLocationUuid = businessInfo?.id;

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);

  const [activeConversation, setActiveConversation] =
    useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);

  // Refs mirror the latest values so the window realtime listener can be bound
  // once (below) without a stale closure over the open thread / location.
  const activeConversationIdRef = useRef<string | null>(null);
  activeConversationIdRef.current = activeConversation?.conversation_id ?? null;
  const locationIdRef = useRef<string | undefined>(businessLocationUuid);
  locationIdRef.current = businessLocationUuid;

  // Replace a conversation in the list by id, or prepend it if it's new; keeps
  // the most-recent-first ordering by lifting a touched thread to the top.
  const upsertConversation = useCallback((conv: Conversation) => {
    setConversations((prev) => {
      const rest = prev.filter(
        (c) => c.conversation_id !== conv.conversation_id,
      );
      return [conv, ...rest];
    });
  }, []);

  const fetchConversations = useCallback(
    async (options: FetchConversationsOptions = {}) => {
      const businessLocationUuid = businessInfo?.id;
      if (!businessLocationUuid) return;
      setConversationsLoading(true);
      try {
        const params = new URLSearchParams({
          business_location_uuid: businessLocationUuid,
        });
        if (options.limit != null) params.set("limit", String(options.limit));
        if (options.offset != null)
          params.set("offset", String(options.offset));
        const url = getApiUrl("MESSAGING_CONVERSATIONS", params.toString());
        const data: ListConversationsResponse = await fetchWithAuth(url);
        setConversations(data.conversations ?? []);
        setTotalUnreadCount(data.unread_count ?? 0);
      } catch (error: any) {
        console.error("Failed to fetch conversations:", error);
      } finally {
        setConversationsLoading(false);
      }
    },
    [fetchWithAuth, businessInfo?.id],
  );

  const fetchThread = useCallback(
    async (conversationId: string, options: FetchThreadOptions = {}) => {
      // Separate the two "loading" shapes so the UI can react differently:
      //   • paging    — a "load earlier" page; the button shows its own spinner.
      //   • switching — opening/switching to a different thread; drives the
      //     branded feed overlay.
      // A background refresh of the already-open thread (e.g. a live inbound)
      // is neither, so the new bubble slots in without a spinner flash.
      const paging = Boolean(options.before);
      const switching =
        !paging && activeConversationIdRef.current !== conversationId;
      if (paging) setLoadingEarlier(true);
      else if (switching) setMessagesLoading(true);
      try {
        const params = new URLSearchParams();
        if (options.limit != null) params.set("limit", String(options.limit));
        if (options.before) params.set("before", options.before);
        const url = getApiUrl(
          "MESSAGING_CONVERSATION_MESSAGES",
          params.toString(),
          { conversationId },
        );
        const data: ThreadResponse = await fetchWithAuth(url);
        setActiveConversation(data.conversation);
        if (options.before) {
          // Load-earlier: prepend the older page above what's already shown.
          setMessages((prev) => [...(data.messages ?? []), ...prev]);
        } else {
          setMessages(data.messages ?? []);
        }
        setHasMoreMessages(Boolean(data.has_more));
      } catch (error: any) {
        console.error("Failed to fetch conversation thread:", error);
      } finally {
        if (paging) setLoadingEarlier(false);
        else if (switching) setMessagesLoading(false);
      }
    },
    [fetchWithAuth],
  );

  const clearThread = useCallback(() => {
    setActiveConversation(null);
    setMessages([]);
    setHasMoreMessages(false);
  }, []);

  const sendMessage = useCallback(
    async (conversationId: string, body: string, media?: MessageMediaRef[]) => {
      const url = getApiUrl("MESSAGING_CONVERSATION_MESSAGES", "", {
        conversationId,
      });
      const data: SendMessageResponse = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify(
          media && media.length ? { body, media } : { body },
        ),
      });
      // Optimistically append to the open thread if it's the active one.
      setMessages((prev) =>
        activeConversation?.conversation_id === conversationId
          ? [...prev, data.message]
          : prev,
      );
      return data;
    },
    [fetchWithAuth, activeConversation?.conversation_id],
  );

  const setReadState = useCallback(
    async (conversationId: string, isUnread: boolean) => {
      const url = getApiUrl("MESSAGING_CONVERSATION_READ_STATE", "", {
        conversationId,
      });
      const data: ReadStateResponse = await fetchWithAuth(url, {
        method: "PUT",
        body: JSON.stringify({ is_unread: isUnread }),
      });
      upsertConversation(data.conversation);
      setTotalUnreadCount(data.unread_count ?? 0);
      setActiveConversation((prev) =>
        prev?.conversation_id === conversationId ? data.conversation : prev,
      );
    },
    [fetchWithAuth, upsertConversation],
  );

  const assignClient = useCallback(
    async (conversationId: string, clientUuid: string | null) => {
      const url = getApiUrl("MESSAGING_CONVERSATION_CLIENT", "", {
        conversationId,
      });
      const data: AssignClientResponse = await fetchWithAuth(url, {
        method: "PUT",
        body: JSON.stringify({ client_uuid: clientUuid }),
      });
      upsertConversation(data.conversation);
      setActiveConversation((prev) =>
        prev?.conversation_id === conversationId ? data.conversation : prev,
      );
    },
    [fetchWithAuth, upsertConversation],
  );

  const createConversation = useCallback(
    async (phone: string, clientUuid?: string | null) => {
      if (!businessLocationUuid) {
        throw new Error("No active location");
      }
      const url = getApiUrl("MESSAGING_CONVERSATIONS", "");
      const data: CreateConversationResponse = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify({
          business_location_uuid: businessLocationUuid,
          phone,
          ...(clientUuid ? { client_uuid: clientUuid } : {}),
        }),
      });
      upsertConversation(data.conversation);
      return data.conversation;
    },
    [fetchWithAuth, businessLocationUuid, upsertConversation],
  );

  const uploadMedia = useCallback(
    async (file: File) => {
      if (!businessLocationUuid) {
        throw new Error("No active location");
      }
      const data_base64 = await fileToBase64(file);
      const url = getApiUrl("MESSAGING_MEDIA", "");
      const data: UploadMediaResponse = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify({
          business_location_uuid: businessLocationUuid,
          content_type: file.type,
          data_base64,
        }),
      });
      return data.media;
    },
    [fetchWithAuth, businessLocationUuid],
  );

  const listScheduled = useCallback(
    async (conversationId: string) => {
      const url = getApiUrl("MESSAGING_CONVERSATION_SCHEDULED", "", {
        conversationId,
      });
      const data: ListScheduledResponse = await fetchWithAuth(url);
      // One call returns BOTH the conversation's chat scheduled sends and the
      // linked client's automated appointment messages (the unified drawer feed).
      return {
        scheduled: data.scheduled ?? [],
        appointmentScheduled: data.appointment_scheduled ?? [],
      };
    },
    [fetchWithAuth],
  );

  const createScheduled = useCallback(
    async (conversationId: string, body: string, scheduledAt: string) => {
      const url = getApiUrl("MESSAGING_CONVERSATION_SCHEDULED", "", {
        conversationId,
      });
      const data: ScheduledResponse = await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify({ body, scheduled_at: scheduledAt }),
      });
      return data.scheduled;
    },
    [fetchWithAuth],
  );

  const updateScheduled = useCallback(
    async (scheduledId: string, patch: UpdateScheduledRequest) => {
      const url = getApiUrl("MESSAGING_SCHEDULED_ITEM", "", { scheduledId });
      const data: ScheduledResponse = await fetchWithAuth(url, {
        method: "PUT",
        body: JSON.stringify(patch),
      });
      return data.scheduled;
    },
    [fetchWithAuth],
  );

  const cancelScheduled = useCallback(
    async (scheduledId: string) => {
      const url = getApiUrl("MESSAGING_SCHEDULED_ITEM", "", { scheduledId });
      const data: ScheduledResponse = await fetchWithAuth(url, {
        method: "DELETE",
      });
      return data.scheduled;
    },
    [fetchWithAuth],
  );

  const sendScheduledNow = useCallback(
    async (scheduledId: string) => {
      const url = getApiUrl("MESSAGING_SCHEDULED_SEND_NOW", "", {
        scheduledId,
      });
      const data: ScheduledResponse = await fetchWithAuth(url, {
        method: "POST",
      });
      // Firing a scheduled send bumps the thread + badge shortly after; refresh
      // the inbox so the list reflects it.
      fetchConversations();
      return data.scheduled;
    },
    [fetchWithAuth, fetchConversations],
  );

  // Whether the two-way inbox is live for this location — the flag AND the
  // location on Twilio AND the messaging permission. Drives the app-wide badge
  // fetch + the realtime subscription; the provider is inert otherwise.
  const chatEnabled =
    isMessagingEnabled() &&
    messagingPlatform === "twilio" &&
    access?.section_permissions?.messaging?.canView === true;

  // Populate the inbox (and the nav unread badge) app-wide when the inbox is
  // live, without waiting for the user to open the Chat page.
  useEffect(() => {
    if (chatEnabled) {
      fetchConversations();
    }
  }, [chatEnabled, fetchConversations]);

  // Live updates: usePusher re-emits the `-messages` channel events onto the
  // window. `message.received` (inbound) and `message.created` (outbound: a
  // scheduled send firing, a booking/appointment confirmation, or a reply from
  // another device/staffer) both refetch authoritatively — the new message isn't
  // in the client yet, and volume is low. `message.status` instead reconciles in
  // place by message_uuid: delivery callbacks arrive in bursts, only affect the
  // open thread, and carry the full status, so a delta avoids a refetch per burst
  // (see applyMessageStatus).
  useEffect(() => {
    const handler = (evt: Event) => {
      const detail = (evt as CustomEvent<MessagingRealtimePayload>).detail;
      if (!detail) return;
      const { name, data } = detail;
      if (
        data.business_location_uuid &&
        locationIdRef.current &&
        data.business_location_uuid !== locationIdRef.current
      ) {
        return; // event for a different location
      }
      const token =
        data.conversation_id ||
        (data.conversation_key
          ? encodeConversationId(data.conversation_key)
          : null);
      const isOpen = !!token && token === activeConversationIdRef.current;
      if (name === "message.received") {
        const refreshed = fetchConversations(); // refresh list + unread badge
        if (isOpen) {
          fetchThread(token as string); // show the new bubble in the open thread
          // Only auto-read when the user is ACTIVELY viewing this thread — the
          // tab must be in the foreground, not merely mounted on the Chat page.
          // A live inbound to a backgrounded tab stays unread; ChatPage re-reads
          // it when the tab regains focus. Sequenced after the refresh so read
          // is the final state (a brief unread→read flicker is expected).
          // fetchConversations never rejects (it catches internally).
          const visible =
            typeof document === "undefined" ||
            document.visibilityState === "visible";
          if (visible) {
            refreshed
              .then(() => setReadState(token as string, false))
              .catch(() => {});
          }
        }
      } else if (name === "message.created") {
        // A new OUTBOUND message (a scheduled send firing, a booking/appointment
        // confirmation, or a reply sent from another device/staffer). Refresh the
        // list authoritatively (reorder + preview) and show it in the open thread.
        // Never clear unread — this is outbound, so a client's unread inbound must
        // stay unread. The open thread already carries the sender's own optimistic
        // bubble by uuid, so the refetch returns the same set (no flicker).
        fetchConversations();
        if (isOpen) fetchThread(token as string);
      } else if (name === "message.status") {
        // Patch the delivery status in place (by message_uuid) rather than
        // refetching: status callbacks arrive in bursts and only matter for the
        // open thread. applyMessageStatus guards against out-of-order events.
        if (isOpen) setMessages((prev) => applyMessageStatus(prev, data));
      }
    };
    window.addEventListener(MESSAGING_REALTIME_EVENT, handler);
    return () => window.removeEventListener(MESSAGING_REALTIME_EVENT, handler);
  }, [fetchConversations, fetchThread, setReadState]);

  const value = useMemo(
    () => ({
      conversations,
      conversationsLoading,
      totalUnreadCount,
      fetchConversations,
      activeConversation,
      messages,
      messagesLoading,
      loadingEarlier,
      hasMoreMessages,
      fetchThread,
      clearThread,
      sendMessage,
      setReadState,
      assignClient,
      createConversation,
      uploadMedia,
      listScheduled,
      createScheduled,
      updateScheduled,
      cancelScheduled,
      sendScheduledNow,
    }),
    [
      conversations,
      conversationsLoading,
      totalUnreadCount,
      fetchConversations,
      activeConversation,
      messages,
      messagesLoading,
      loadingEarlier,
      hasMoreMessages,
      fetchThread,
      clearThread,
      sendMessage,
      setReadState,
      assignClient,
      createConversation,
      uploadMedia,
      listScheduled,
      createScheduled,
      updateScheduled,
      cancelScheduled,
      sendScheduledNow,
    ],
  );

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
}

export function useMessaging() {
  const context = useContext(MessagingContext);
  if (!context) {
    throw new Error("useMessaging must be used within MessagingProvider");
  }
  return context;
}

/**
 * Non-throwing variant for consumers that render whether or not the provider is
 * mounted (e.g. the NavBar unread badge). Returns null when there is no provider.
 */
export function useOptionalMessaging(): MessagingContextType | null {
  return useContext(MessagingContext) ?? null;
}
